import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import './FeaturesOfLaunchway.css';

/* ── Tilt handlers (module-level, no hooks needed) ── */
function handleTilt(e) {
  const el = e.currentTarget;
  const rect = el.getBoundingClientRect();
  const x = ((e.clientX - rect.left) / rect.width  - 0.5) *  9;
  const y = ((e.clientY - rect.top)  / rect.height - 0.5) * -9;
  el.style.transition = 'transform 0.08s linear';
  el.style.transform  = `perspective(700px) rotateX(${y}deg) rotateY(${x}deg) translateZ(10px)`;
}
function handleTiltLeave(e) {
  const el = e.currentTarget;
  el.style.transition = 'transform 0.55s ease';
  el.style.transform  = '';
}

/* ── Landing navbar ── */
const NAV_LINKS = [
  { label: 'How It Works', id: 'lwf-sec-spectrum'   },
  { label: 'vs Others',    id: 'lwf-sec-compare'    },
  { label: 'Features',     id: 'lwf-sec-features'   },
  { label: 'See Demo',     id: 'lwf-sec-videos'     },
  { label: 'Feedback',     id: 'lwf-sec-reactions'  },
];

function LandingNav() {
  const navigate   = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  function scrollTo(id) {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <nav className={`lwf-nav${scrolled ? ' lwf-nav-scrolled' : ''}`}>
      <div className="lwf-nav-inner">
        <button className="lwf-nav-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src="/assets/launchwaylogo.png" className="lwf-nav-logo-img" alt="" />
          <span>Launch<span className="lwf-nav-logo-accent">way</span></span>
        </button>

        <div className="lwf-nav-links">
          {NAV_LINKS.map((l) => (
            <button key={l.id} className="lwf-nav-link" onClick={() => scrollTo(l.id)}>
              {l.label}
            </button>
          ))}
        </div>

        <div className="lwf-nav-cta">
          <button className="lwf-nav-signin" onClick={() => navigate('/login')}>Sign In</button>
          <button className="lwf-nav-getstarted" onClick={() => navigate('/signup')}>Get Started Free</button>
        </div>

        <button
          className={`lwf-hamburger${menuOpen ? ' lwf-hamburger-open' : ''}`}
          onClick={() => setMenuOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          <span /><span /><span />
        </button>
      </div>

      {menuOpen && (
        <div className="lwf-mobile-menu">
          {NAV_LINKS.map((l) => (
            <button key={l.id} className="lwf-mobile-link" onClick={() => scrollTo(l.id)}>
              {l.label}
            </button>
          ))}
          <div className="lwf-mobile-actions">
            <button className="lwf-mobile-signin" onClick={() => { setMenuOpen(false); navigate('/login'); }}>
              Sign In
            </button>
            <button className="lwf-mobile-getstarted" onClick={() => { setMenuOpen(false); navigate('/signup'); }}>
              Get Started Free
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}

/* ── Landing footer ── */
function LandingFooter() {
  return (
    <footer className="lwf-footer">
      <div className="lwf-footer-inner">
        <div className="lwf-footer-brand">
          <span className="lwf-footer-logo">Launch<span className="lwf-footer-logo-accent">way</span></span>
          <p className="lwf-footer-tagline">
            Finds better roles, tailors your real resume, and helps you apply step by step.
          </p>
        </div>
        <div className="lwf-footer-links">
          <Link to="/privacy-policy">Privacy Policy</Link>
          <Link to="/terms-of-service">Terms of Service</Link>
          <a href="mailto:launchway@tailor-mycareer.online">Contact</a>
        </div>
      </div>
      <div className="lwf-footer-bottom">
        <p>© {new Date().getFullYear()} Launchway. All rights reserved.</p>
      </div>
    </footer>
  );
}

/* ── Side progress nav ── */
const PROGRESS_SECTIONS = [
  { id: 'lwf-sec-spectrum',  label: 'Where It Fits'  },
  { id: 'lwf-sec-compare',   label: 'Comparison'     },
  { id: 'lwf-sec-features',  label: 'Features'       },
  { id: 'lwf-sec-videos',    label: 'See It Live'    },
  { id: 'lwf-sec-reactions', label: 'Your Take'      },
];

function ProgressNav({ active }) {
  function scrollTo(id) {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  return (
    <nav className="lwf-progress-nav" aria-label="Page sections">
      {PROGRESS_SECTIONS.map((s) => (
        <button
          key={s.id}
          className={`lwf-prog-dot${active === s.id ? ' lwf-prog-dot-active' : ''}`}
          onClick={() => scrollTo(s.id)}
          aria-label={s.label}
        >
          <span className="lwf-prog-label">{s.label}</span>
        </button>
      ))}
    </nav>
  );
}

function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('lwf-in');
          obs.unobserve(el);
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return ref;
}

const SPECTRUM = [
  {
    label: 'Basic Autofill Tools',
    sub: 'Simplify, etc.',
    items: [
      'Fills fields on the current page only',
      'You still find jobs yourself',
      'No resume tailoring',
    ],
    color: '#fc8181',
    good: false,
  },
  {
    label: 'Launchway',
    sub: 'You are here',
    items: [
      'Find better jobs, not more jobs',
      <>Tailor resume using real experience<sup className="lwf-footnote-mark">✦</sup></>,
      'Stay in control - no blind applying',
      'Your own cover letter style, every time',
    ],
    color: '#ffd57a',
    good: true,
    center: true,
  },
  {
    label: 'Auto Apply Bots',
    sub: 'Fills and submits entire applications',
    items: [
      'Submits to everything, no control',
      'Stuck applications are discarded',
      'You start over from scratch if it fails',
    ],
    color: '#fc8181',
    good: false,
  },
];

const COMPARISON_ROWS = [
  {
    label: 'Finds & ranks jobs by fit, not just keywords',
    fieldFiller: false, fullAuto: true, launchway: true,
  },
  {
    label: 'Runs continuously, applies automatically',
    tooltip: 'Launchway finds jobs and starts applications in the background. Unlike full-auto bots, it pauses when human input is needed - so you stay in control.',
    fieldFiller: false, fullAuto: true, launchway: true, lwBadge: 'Guided',
  },
  {
    label: 'Tailors resume using only real experience',
    fieldFiller: false, fullAuto: false, launchway: true,
  },
  {
    label: 'Keeps your original resume layout intact',
    tooltip: 'Currently works with Google Docs resumes. More formats planned.',
    fieldFiller: null, fullAuto: false, launchway: true, lwBadge: 'Google Docs only',
  },
  {
    label: 'Recovers stuck applications',
    fieldFiller: null, fullAuto: false, launchway: true, lwBadge: 'Improving',
  },
  {
    label: 'User control',
    controlLabels: {
      fieldFiller: { text: 'Full control',        style: 'positive' },
      fullAuto:    { text: 'No control',           style: 'negative' },
      launchway:   { text: 'Take control anytime', style: 'positive' },
    },
  },
];

const FEATURES = [
  {
    iconPath: '/assets/icon-resume.png',
    badge: 'Formatting Preserved',
    badgeColor: 'cyan',
    title: 'Your Resume Stays Exactly How You Designed It',
    body: 'No broken formatting. No templates forced on you. Your fonts, layout, and spacing stay completely intact. Launchway tailors keywords and bullet points inside Google Docs without touching your design.',
    callout: null,
  },
  {
    iconPath: '/assets/icon-pen.png',
    badge: 'Template-Based',
    badgeColor: 'cyan',
    title: 'Cover Letters That Sound Like You Wrote Them',
    body: "Give Launchway a template or a few instructions. Every letter is written for that specific role - not a recycled AI dump that looks identical to every other candidate's opening paragraph.",
    callout: null,
  },
  {
    iconPath: '/assets/icon-target.png',
    badge: 'Profile-Matched',
    badgeColor: 'cyan',
    title: 'A Shortlist of the Best Fits, Not 500 Listings',
    body: "Launchway finds relevant roles and ranks them by how well they match your actual profile. You get fewer, better options - not a flood of loosely related jobs you have to sort through yourself.",
    callout: null,
  },
  {
    iconPath: '/assets/icon-shield.png',
    badge: 'Recovery Mode',
    badgeColor: 'cyan',
    title: 'Hit a Wall? Continue - Not Start Over.',
    body: "Hit a CAPTCHA, an unusual question, or a tricky step? Launchway pauses and hands you control at exactly that point. You handle it, Launchway picks up and keeps going. You never lose your progress.",
    callout: null,
  },
];


function Check({ val, note, lwBadge }) {
  if (val === null) return <span className="lwf-check-na" title="Not applicable to this tool's approach">N/A</span>;
  if (val === 'partial') {
    return (
      <div className="lwf-pill lwf-pill-partial">
        <span className="lwf-pill-partial-icon">◑</span>
        <span className="lwf-pill-label">Partial</span>
      </div>
    );
  }
  if (val === true) {
    return (
      <div className="lwf-pill-wrap">
        <div className="lwf-pill lwf-pill-yes">
          <img src="/assets/check.png" alt="yes" className="lwf-pill-img" />
          <span className="lwf-pill-label">
            {note || 'Available'}
          </span>
        </div>
        {lwBadge && (
          <span className={`lwf-lw-badge ${lwBadge === 'Improving' ? 'lwf-lw-badge-improving' : 'lwf-lw-badge-partial'}`}>
            {lwBadge}
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="lwf-pill lwf-pill-no">
      <img src="/assets/cross.png" alt="no" className="lwf-pill-img" />
      <span className="lwf-pill-label">Not available</span>
    </div>
  );
}

/* ── Reaction options ── */
const EMOJI_ICONS = {
  '🔥': '/assets/icon-flat-fire.png',
  '👀': '/assets/icon-flat-eyes.png',
  '🤔': '/assets/icon-flat-thinking.png',
  '😬': '/assets/icon-flat-cancel.png',
};

const REACTION_OPTIONS = [
  { emoji: '🔥', label: 'I need this right now' },
  { emoji: '👀', label: "I'm keeping an eye on it" },
  { emoji: '🤔', label: 'I still have questions' },
  { emoji: '😬', label: "Not for me" },
];

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/* ── Jar color system ── */
const REACTION_COLORS = {
  '🔥': [255, 107, 53],
  '👀': [93, 141, 246],
  '🤔': [251, 191, 36],
  '😬': [148, 163, 184],
};

function blendColors(reactions) {
  if (!reactions.length) return [6, 182, 212];
  const counts = { '🔥': 0, '👀': 0, '🤔': 0, '😬': 0 };
  reactions.forEach((r) => { if (counts[r.emoji] !== undefined) counts[r.emoji]++; });
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return [0, 1, 2].map((ch) =>
    Math.round(
      Object.entries(counts).reduce(
        (sum, [emoji, cnt]) => sum + (REACTION_COLORS[emoji]?.[ch] || 0) * (cnt / total),
        0
      )
    )
  );
}

function getSentiment(reactions) {
  if (!reactions.length) return 'Be the first to drop a reaction into the jar.';
  const counts = { '🔥': 0, '👀': 0, '🤔': 0, '😬': 0 };
  reactions.forEach((r) => { if (counts[r.emoji] !== undefined) counts[r.emoji]++; });
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
  const messages = {
    '🔥': 'The jar is running hot - most people said they need this right now.',
    '👀': 'The jar is glowing blue - most visitors are watching closely.',
    '🤔': 'The jar looks amber - most people still have questions.',
    '😬': 'The jar is cooling off - most said it\'s not for them yet. We\'re taking notes.',
  };
  return messages[top];
}

/* ── Reaction Jar ── */
function ReactionJar({ reactions, totalReactions, ripple, bodyRef }) {
  const rgb = blendColors(reactions);
  const ca  = (a) => `rgba(${rgb.join(',')},${a})`;
  const fillPercent = totalReactions === 0
    ? 0
    : Math.min(8 + totalReactions * 3.5, 88);

  return (
    <div className="lwf-jar-wrap">
      <div className={`lwf-jar${ripple ? ' lwf-jar-ripple' : ''}`}>
        <div className="lwf-jar-neck" />
        <div className="lwf-jar-body" ref={bodyRef}>
          <div
            className="lwf-jar-fill"
            style={{ height: `${fillPercent}%`, background: `linear-gradient(180deg, ${ca(0.45)} 0%, ${ca(0.82)} 100%)` }}
          >
            <div className="lwf-jar-wave"  style={{ background: ca(0.55) }} />
            <div className="lwf-jar-wave lwf-jar-wave-2" style={{ background: ca(0.35) }} />
            <div className="lwf-jar-bubble lwf-jar-b1" style={{ background: ca(0.4) }} />
            <div className="lwf-jar-bubble lwf-jar-b2" style={{ background: ca(0.3) }} />
            <div className="lwf-jar-bubble lwf-jar-b3" style={{ background: ca(0.35) }} />
          </div>
        </div>
      </div>
      <div className="lwf-jar-count">
        {totalReactions > 0
          ? `${totalReactions} reaction${totalReactions !== 1 ? 's' : ''}`
          : 'Waiting for first drop…'}
      </div>
    </div>
  );
}

function FeaturesOfLaunchway() {
  const navigate = useNavigate();
  const [activeSection,     setActiveSection]        = useState('');
  const [openRowTooltip,    setOpenRowTooltip]       = useState(null);
  const [reactions,       setReactions]       = useState([]);
  const [totalReactions,  setTotalReactions]  = useState(0);
  const [visitorCount,    setVisitorCount]    = useState(null);
  const isDev = process.env.NODE_ENV === 'development';
  const [hasReacted,   setHasReacted]   = useState(() => {
    if (isDev) return false;
    try { return !!localStorage.getItem('lwf_my_reaction'); } catch { return false; }
  });
  const [myReaction,   setMyReaction]   = useState(() => {
    if (isDev) return null;
    try { const s = localStorage.getItem('lwf_my_reaction'); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [jarRipple,   setJarRipple]   = useState(false);
  const [dropState,   setDropState]   = useState(null);
  const [splashState, setSplashState] = useState(null);
  const cursorRef   = useRef(null);
  const columnRef   = useRef(null);
  const machineRef  = useRef(null);
  const jarBodyRef  = useRef(null);
  const spectrumRef = useReveal(0.08);
  const compRef     = useReveal(0.04);
  const featRef     = useReveal(0.04);
  const vidRef      = useReveal(0.04);
  const reactRef    = useReveal(0.06);

  /* Cursor light */
  useEffect(() => {
    const el = cursorRef.current;
    if (!el) return;
    const move = (e) => {
      el.style.left = `${e.clientX}px`;
      el.style.top  = `${e.clientY}px`;
    };
    window.addEventListener('mousemove', move, { passive: true });
    return () => window.removeEventListener('mousemove', move);
  }, []);

  /* Active section tracker for progress dots */
  useEffect(() => {
    const observers = PROGRESS_SECTIONS.map(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return null;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActiveSection(id); },
        { rootMargin: '-35% 0px -35% 0px' }
      );
      obs.observe(el);
      return obs;
    }).filter(Boolean);
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  /* Fetch reactions + record visit on mount */
  useEffect(() => {
    fetch(`${API_BASE}/api/page-reactions`)
      .then((r) => r.json())
      .then((data) => {
        setReactions(data.reactions || []);
        setTotalReactions(data.total_reactions ?? (data.reactions?.length ?? 0));
        setVisitorCount(data.visitor_count ?? null);
      })
      .catch(() => {});

    fetch(`${API_BASE}/api/page-visits`, { method: 'POST' })
      .then((r) => r.json())
      .then((data) => { if (data.visitor_count) setVisitorCount(data.visitor_count); })
      .catch(() => {});
  }, []);

  function handleDrop(opt) {
    if (hasReacted || dropState) return;
    if (!columnRef.current || !machineRef.current || !jarBodyRef.current) {
      handleReact(opt);
      return;
    }
    const colRect     = columnRef.current.getBoundingClientRect();
    const machineRect = machineRef.current.getBoundingClientRect();
    const jarRect     = jarBodyRef.current.getBoundingClientRect();

    const startY = machineRect.bottom - colRect.top;
    const fillPct = totalReactions === 0 ? 0 : Math.min(8 + totalReactions * 3.5, 88);
    const liquidSurface = jarRect.top - colRect.top + jarRect.height * (1 - fillPct / 100);
    const endY = liquidSurface - 4;

    setDropState({ emoji: opt.emoji, startY, endY, key: Date.now() });

    setTimeout(() => {
      setDropState(null);
      setSplashState({ y: endY, key: Date.now() });
      setJarRipple(true);
      handleReact(opt);
      setTimeout(() => {
        setSplashState(null);
        setJarRipple(false);
      }, 650);
    }, 820);
  }

  async function handleReact(option) {
    try {
      const res = await fetch(`${API_BASE}/api/page-reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emoji: option.emoji }),
      });
      if (res.status === 409) {
        const data = await res.json();
        const stored = { ...data.reaction };
        setMyReaction(stored);
        try { localStorage.setItem('lwf_my_reaction', JSON.stringify(stored)); } catch {}
      } else if (res.ok) {
        const data = await res.json();
        const stored = { emoji: option.emoji, label: option.label, reaction_number: data.reaction_number };
        setMyReaction(stored);
        setReactions((prev) => [{ id: Date.now(), emoji: option.emoji, label: option.label }, ...prev]);
        setTotalReactions((prev) => prev + 1);
        try { localStorage.setItem('lwf_my_reaction', JSON.stringify(stored)); } catch {}
      }
      setHasReacted(true);
    } catch {
      setHasReacted(true);
    }
  }

  return (
    <div className="lwf-page">
      <LandingNav />
      <div className="lwf-cursor-light" ref={cursorRef} aria-hidden="true" />
      <ProgressNav active={activeSection} />

      {/* ── Hero ── */}
      <header className="lwf-hero">
        <div className="lwf-hero-badge">
          Free to try · No credit card
          {visitorCount > 0 && <span className="lwf-hero-visitors"> · Seen by {visitorCount.toLocaleString()} people</span>}
        </div>
        <h1>
          Apply to Better Jobs,<br />
          <span className="lwf-hero-gradient">Faster and Smarter</span>
        </h1>
        <p className="lwf-hero-sub-hook">Not a spam bot. Not just a form filler.</p>
        {/* <p className="lwf-hero-tagline">
          Most tools optimize for volume. Launchway optimizes for interview quality.
        </p> */}
        <div className="lwf-hero-cta">
          <button onClick={() => navigate('/signup')}>Get Started Free</button>
          <button className="secondary" onClick={() => navigate('/login')}>Sign In</button>
        </div>
        <div className="lwf-hero-glow" />
        <div className="lwf-scroll-cue" aria-hidden="true">
          <span className="lwf-scroll-cue-text">Not convinced yet?</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </header>

      {/* ── Spectrum ── */}
      <section id="lwf-sec-spectrum" className="lwf-spectrum-section lwf-reveal" ref={spectrumRef}>
        <div className="lwf-chapter">01</div>
        <h2 className="lwf-section-title">Where Does Launchway Fit?</h2>
        <p className="lwf-section-sub">
          Most tools either do too little or too much. Launchway sits in between - guided,
          not blind. Your applications look like you wrote them, because they reflect what
          you've actually done.
        </p>
        <div className="lwf-spectrum-grid">
          {SPECTRUM.map((item, index) => (
            <div
              key={item.label}
              className={`lwf-spectrum-card${item.center ? ' lwf-spectrum-center' : ''}`}
              style={{ '--accent': item.color, '--i': index }}
              onMouseMove={handleTilt}
              onMouseLeave={handleTiltLeave}
            >
              {item.center && <div className="lwf-spectrum-you-badge">★ YOU ARE HERE</div>}
              <h3 style={{ color: item.color }}>{item.label}</h3>
              <div className="lwf-spectrum-sub-label">{item.sub}</div>
              <ul>
                {item.items.map((pt) => (
                  <li key={pt} className={item.good ? 'lwf-pt-good' : 'lwf-pt-bad'}>
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── Comparison table ── */}
      <section id="lwf-sec-compare" className="lwf-comp-section lwf-reveal" ref={compRef}>
        <div className="lwf-chapter">02</div>
        <h2 className="lwf-section-title">How Launchway Compares</h2>
        <div className="lwf-comp-wrap">
          <div className="lwf-comp-table" onClick={() => setOpenRowTooltip(null)}>
            <div className="lwf-comp-row lwf-comp-head">
              <div className="lwf-comp-feat-cell" />
              <div className="lwf-comp-cell">
                <strong>Basic Autofill Tools</strong>
                <span>Populates fields on the current page</span>
              </div>
              <div className="lwf-comp-cell">
                <strong>Auto Apply Bots</strong>
                <span>Fills and submits entire applications</span>
              </div>
              <div className="lwf-comp-cell lwf-comp-lw">
                <strong>Launchway</strong>
                <span>★ Guided agent</span>
              </div>
            </div>
            {COMPARISON_ROWS.map((row, i) => (
              <div key={i} className={`lwf-comp-row${row.controlLabels ? ' lwf-comp-row-control' : ''}`} style={{ '--i': i }}>
                <div className="lwf-comp-feat-cell">
                  {row.label}
                  {row.tooltip && (
                    <span className="lwf-row-tooltip-wrap">
                      <button
                        className="lwf-row-tooltip"
                        aria-label="Show info"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenRowTooltip((prev) => (prev === i ? null : i));
                        }}
                      >
                        ⓘ
                      </button>
                      {openRowTooltip === i && (
                        <span className="lwf-row-tooltip-pop">
                          {row.tooltip}
                        </span>
                      )}
                    </span>
                  )}
                </div>
                {row.controlLabels ? (
                  <>
                    <div className="lwf-comp-cell">
                      <span className={`lwf-ctrl-badge lwf-ctrl-${row.controlLabels.fieldFiller.style}`}>{row.controlLabels.fieldFiller.text}</span>
                    </div>
                    <div className="lwf-comp-cell">
                      <span className={`lwf-ctrl-badge lwf-ctrl-${row.controlLabels.fullAuto.style}`}>{row.controlLabels.fullAuto.text}</span>
                    </div>
                    <div className="lwf-comp-cell lwf-comp-lw">
                      <span className={`lwf-ctrl-badge lwf-ctrl-${row.controlLabels.launchway.style}`}>{row.controlLabels.launchway.text}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="lwf-comp-cell"><Check val={row.fieldFiller} /></div>
                    <div className="lwf-comp-cell"><Check val={row.fullAuto} /></div>
                    <div className="lwf-comp-cell lwf-comp-lw">
                      <Check val={row.launchway} note={row.note} lwBadge={row.lwBadge} />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Feature deep-dives ── */}
      <section id="lwf-sec-features" className="lwf-feat-section lwf-reveal" ref={featRef}>
        <div className="lwf-chapter">03</div>
        <h2 className="lwf-section-title">What to Expect</h2>
        <div className="lwf-feat-grid">
          {FEATURES.map((f, index) => (
            <article key={f.title} className="lwf-feat-card" style={{ '--i': index }}
              onMouseMove={handleTilt} onMouseLeave={handleTiltLeave}>
              <div className="lwf-feat-top">
                <img src={f.iconPath} alt="" className="lwf-feat-icon" />
                <div>
                  <span className={`lwf-feat-badge lwf-badge-${f.badgeColor}`}>{f.badge}</span>
                  <h3>{f.title}</h3>
                </div>
              </div>
              <p>{f.body}</p>
              {f.callout && (
                <div className="lwf-callout">
                  <span className="lwf-callout-tag">⚡ vs Others</span>
                  {f.callout}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      {/* ── Videos ── */}
      <section id="lwf-sec-videos" className="lwf-vid-section lwf-reveal" ref={vidRef}>
        <div className="lwf-chapter">04</div>
        <h2 className="lwf-section-title">See It in Action</h2>
        <p className="lwf-section-sub">
          See how a <span className="lwf-cyan-em">20-minute</span> application becomes a <span className="lwf-cyan-em">5-minute</span> flow.
        </p>
        <div className="lwf-vid-grid">
          <div className="lwf-vid-card">
            <div className="lwf-vid-badge">Resume Tailoring</div>
            <h3>Format Preserved. Content Tailored.</h3>
            <p>Watch Launchway adjust a resume for a specific role, inside Google Docs, without touching your layout or design.</p>
          <video controls preload="metadata" className="lwf-demo-video">
            <source src="/assets/documentation/resume_tailoring_edited.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
          <div className="lwf-vid-card">
            <div className="lwf-vid-badge">Auto Apply</div>
            <h3>Apply. Hit a Wall. Continue. Not Start Over.</h3>
            <p>Watch Launchway navigate a full job application and see how it hands back control without discarding your work.</p>
          <video controls preload="metadata" className="lwf-demo-video">
            <source src="/assets/documentation/job_application_demo_edited.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          </div>
        </div>
      </section>

      {/* ── Reaction Jar ── */}
      <section id="lwf-sec-reactions" className="lwf-react-section lwf-reveal" ref={reactRef}>
        <div className="lwf-chapter">05</div>
        <h2 className="lwf-section-title">What Do You Think?</h2>
        <p className="lwf-section-sub">One tap. Helps us know if this is resonating.</p>

        {/* Machine → Pipe → Jar column */}
        <div className="lwf-reactor-column" ref={columnRef}>

          {/* Glass machine */}
          <div className="lwf-machine" ref={machineRef}>
            <p className="lwf-machine-label">Drop your reaction</p>
            <div className="lwf-machine-btns">
              {REACTION_OPTIONS.map((opt) => (
                <button
                  key={opt.emoji}
                  className={`lwf-machine-btn${hasReacted || dropState ? ' lwf-machine-btn-done' : ''}`}
                  onClick={() => handleDrop(opt)}
                  disabled={hasReacted || !!dropState}
                >
                  <span className="lwf-react-emoji">
                    <img src={EMOJI_ICONS[opt.emoji]} alt={opt.label} className="lwf-react-icon" />
                  </span>
                  <span className="lwf-react-label">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Nozzle + pipe connecting machine to jar */}
          <div className="lwf-nozzle" />
          <div className="lwf-pipe-tube" />

          {/* Jar */}
          <ReactionJar reactions={reactions} totalReactions={totalReactions} ripple={jarRipple} bodyRef={jarBodyRef} />

          {/* Animated drop */}
          {dropState && (
            <span
              className="lwf-col-drop"
              key={dropState.key}
              style={{
                top: `${dropState.startY}px`,
                '--travel-y': `${dropState.endY - dropState.startY}px`,
              }}
            >
              <img src={EMOJI_ICONS[dropState.emoji]} alt="" className="lwf-drop-icon" />
            </span>
          )}

          {/* Splash rings */}
          {splashState && (
            <div
              className="lwf-splash"
              key={splashState.key}
              style={{ top: `${splashState.y}px` }}
            />
          )}
        </div>

        <p className="lwf-jar-sentiment">{getSentiment(reactions)}</p>

        {hasReacted && myReaction && (
          <div className="lwf-react-confirmed">
            <span><img src={EMOJI_ICONS[myReaction.emoji]} alt="" className="lwf-confirmed-icon" /></span>
            <span>
              {myReaction.reaction_number
                ? `You are reaction #${myReaction.reaction_number}`
                : 'Your reaction was counted'}
            </span>
          </div>
        )}
      </section>

      {/* ── CTA ── */}
      <section className="lwf-cta-section">
        <h2>Stop wasting hours on applications.</h2>
        <p>Start applying smarter in minutes. Free to start - no templates forced, no progress lost.</p>
        <div className="lwf-hero-cta">
          <button onClick={() => navigate('/signup')}>Create Free Account</button>
          <button className="secondary" onClick={() => navigate('/login')}>Sign In</button>
        </div>
        <p className="lwf-cta-trust">Early beta · Built with real job seekers · No credit card needed</p>
      </section>

      <LandingFooter />

    </div>
  );
}

export default FeaturesOfLaunchway;
